import moment from 'moment';
import type { FundDataRow } from '../components/ReportTables/types';
import commonStore from '../../../../store/common';
import { isFcaTableDisabledForFund } from './visibleSections';

interface SelectOption {
  value: unknown;
  label?: unknown;
}

interface RowHeightCell {
  value: unknown;
  error?: unknown;
}

interface RowHeightParams {
  label: unknown;
  labelWidth: number;
  value?: unknown;
  error?: unknown;
  cells?: RowHeightCell[];
  valueWidth: number;
  hasWarning?: boolean;
}

interface CellWidthsParams {
  isMultiFunds?: boolean;
  isComparisonReport?: boolean;
  fundSize: number;
  contentWidth: number;
}

interface CellWidthsResult {
  labelWidth: number;
  valueWidth: number;
}

type ColumnType =
  'select' | 'customDate' | 'customDateTime' | 'customText' | 'customNumber' | 'customBoolean';

type ColumnValueKey = 'selectedValue' | 'text' | 'value' | 'date';

interface ReportTableRow extends FundDataRow {
  errors?: Record<string, unknown>;
  warning?: Record<string, unknown>;
  static_template_fields?: string[];
}

interface ColumnByTypeParams {
  value: ReportTableRow | undefined;
  key: string;
  type?: string;
  options?: unknown[];
  defaultValue?: unknown;
  index: number;
  required?: boolean;
  [key: string]: unknown;
}

interface UpdateCell {
  type?: string;
  selectedValue?: string | boolean | null;
  checked?: boolean;
  text?: string;
  value?: unknown;
  date?: Date | null;
}

interface UpdateDataByTypeParams {
  cell: UpdateCell;
}

interface SchemaValueDescriptor {
  type?: string | string[];
  format?: string;
  [key: string]: unknown;
}

function isValidDate(d: unknown): d is Date {
  return d instanceof Date && !Number.isNaN(d.getTime());
}

const PARAGRAPH_LINE_HEIGHT = 17;
const DEFAULT_ROW_HEIGHT = 32;
const WARNING_EXTRA_HEIGHT = 20;

// Cap to bound memory if many distinct (width, text) pairs appear; in
// practice the same error/warning text and the same column widths repeat
// across funds and re-renders, so the cache saturates quickly and stays warm.
const MEASUREMENT_CACHE_LIMIT = 10000;

// Cached paragraph height — measuring used to create + append + measure +
// remove a <p> per call (two layout reflows). We now create the element once
// and reuse it, and cache results by `width|text` — typical reports re-measure
// the same content many times across funds, columns, and re-renders.
const __paragraphHeightCache = new Map<string, number>();
let __paragraphHeightEl: HTMLParagraphElement | null = null;

export function getTextHeightForParagraph(text: string, width: number): number {
  const key = `${width}|${text}`;
  const cached = __paragraphHeightCache.get(key);
  if (cached !== undefined) return cached;

  if (!__paragraphHeightEl) {
    __paragraphHeightEl = document.createElement('p');
    __paragraphHeightEl.style.cssText =
      'position:fixed;top:0;left:0;visibility:hidden;font-size:12px;line-height:17px;' +
      'word-break:break-word;overflow-wrap:break-word;padding:6px 8px;' +
      'white-space:normal;box-sizing:border-box';
    document.body.appendChild(__paragraphHeightEl);
  }
  __paragraphHeightEl.style.width = `${width - 16}px`;
  __paragraphHeightEl.innerHTML = text;

  const measured = __paragraphHeightEl.getBoundingClientRect().height;
  const result = measured === 0 ? 40 : measured + 18;

  if (__paragraphHeightCache.size >= MEASUREMENT_CACHE_LIMIT) __paragraphHeightCache.clear();
  __paragraphHeightCache.set(key, result);
  return result;
}

// Same approach as the paragraph cache above — one reused hidden element and a
// (width, text) → lines cache. The same text wraps to the same line count at
// the same width, so the first measurement is reused for every later call.
const __segmentLineCache = new Map<string, number>();
let __segmentLineEl: HTMLParagraphElement | null = null;

// Measures how many lines a single plain-text segment wraps into in `width`px.
function measureSegmentLines(text: string, width: number): number {
  const key = `${width}|${text}`;
  const cached = __segmentLineCache.get(key);
  if (cached !== undefined) return cached;

  if (!__segmentLineEl) {
    __segmentLineEl = document.createElement('p');
    __segmentLineEl.style.cssText =
      'position:fixed;top:0;left:0;visibility:hidden;font-size:12px;line-height:' +
      `${PARAGRAPH_LINE_HEIGHT}px;word-break:break-word;overflow-wrap:break-word;` +
      'white-space:normal;box-sizing:border-box';
    document.body.appendChild(__segmentLineEl);
  }
  __segmentLineEl.style.width = `${width - 16}px`;
  __segmentLineEl.textContent = text;

  const lines = Math.max(
    1,
    Math.round(__segmentLineEl.getBoundingClientRect().height / PARAGRAPH_LINE_HEIGHT)
  );

  if (__segmentLineCache.size >= MEASUREMENT_CACHE_LIMIT) __segmentLineCache.clear();
  __segmentLineCache.set(key, lines);
  return lines;
}

// Counts how many lines `text` occupies when rendered in a cell of `width`px.
// `<br/>` markers (e.g. a stacked error + warning message) are treated as
// explicit line breaks. Returns at least 1 — an empty cell still takes a line.
export function getLineCount(text: unknown, width: number): number {
  if (!text) return 1;

  return String(text)
    .split(/<br\s*\/?>/i)
    .reduce((total, segment) => total + measureSegmentLines(segment, width), 0);
}

// Computes a table row height from its content. A single line of text renders
// at 32px; each extra wrapped line adds one line-height. The label sits in its
// own column; every value column stacks its error/warning message under its
// own value, so each cell needs value-lines + error-lines. The row fits the
// tallest cell (or the label, whichever is taller).
//
// Pass either a single `value`/`error` pair, or `cells` — an array of
// `{ value, error }` pairs, one per value column. `cells` is required for
// multi-column rows: taking `max(valueLines)` and `max(errorLines)` separately
// across columns pairs a long value with a different column's long error and
// over-sizes the row.
export function getRowHeight({
  label,
  labelWidth,
  value,
  error,
  cells,
  valueWidth,
  hasWarning = false
}: RowHeightParams): number {
  const labelLines = getLineCount(label, labelWidth);

  const valueCells = cells ?? [{ value, error }];
  const cellLines = valueCells.map(
    cell =>
      getLineCount(cell.value, valueWidth) + (cell.error ? getLineCount(cell.error, valueWidth) : 0)
  );

  const rowLines = Math.max(labelLines, ...cellLines);

  return (
    DEFAULT_ROW_HEIGHT +
    (rowLines - 1) * PARAGRAPH_LINE_HEIGHT +
    (hasWarning ? WARNING_EXTRA_HEIGHT : 0)
  );
}

// Resolves the rendered label/value column widths for a report table, matching
// the layout the `columns` memo builds. The hidden line-count measurement must
// use the same width the cell is actually rendered at, otherwise text wraps a
// different number of times in the measurement than on screen.
export function getCellWidths({
  isMultiFunds,
  isComparisonReport,
  fundSize,
  contentWidth
}: CellWidthsParams): CellWidthsResult {
  if (!isMultiFunds) {
    const width = contentWidth / (isComparisonReport ? 3 : 2);
    return { labelWidth: width, valueWidth: width };
  }

  if (fundSize <= 3 && !isComparisonReport) {
    const width = contentWidth / (fundSize + 1);
    return { labelWidth: width, valueWidth: width };
  }

  return { labelWidth: 200, valueWidth: 300 };
}

export function getBiggestString(strings: string[] | undefined | null): string {
  if (!strings || strings?.length === 0) return ''; // Return empty if array is empty

  return strings.reduce(
    (longest, current) => (current.length > longest.length ? current : longest),
    strings[0]
  );
}

// Resolves a stored cell value to the text the user actually sees: for
// option-backed fields (select) the matching option label, otherwise the value
// itself. Used so row-height measurements match the rendered cell — measuring
// the raw option code instead of its (often much longer) label clips the row.
export function getDisplayValue(value: unknown, options?: SelectOption[]): string {
  if (options) {
    return value ? String(options.find(option => option.value === value)?.label ?? '') : '';
  }
  return value != null ? String(value) : '';
}

// Joins a cell's error and warning messages into the single string it renders,
// stacking them with an explicit line break when both are present.
export function combineMessage(errorMsg: unknown, warningMsg: unknown): string {
  const error: string = (errorMsg || '') as string;
  const warning: string = (warningMsg || '') as string;
  if (error && warning) return `${error}<br/>${warning}`;
  return error || warning;
}

// Builds getRowHeight's `cells` — one { value, error } pair per value column.
// `rows` holds the data object behind each column (one per fund, or a single
// element for single-fund tables). Pairing each column's own value with its
// own error lets getRowHeight size the row to the tallest cell instead of
// summing the longest value with an unrelated column's longest error.
// `includeWarning` is false for tables that account for warnings separately.
export function getRowCells(
  rows: Array<ReportTableRow | undefined>,
  key: string,
  options: unknown[] | undefined,
  includeWarning: boolean = true
): Array<{ value: string; error: string }> {
  return rows.map(row => ({
    value: getDisplayValue(row?.[key], options as SelectOption[] | undefined),
    error: includeWarning
      ? combineMessage(
          (row?.errors?.[key] as { msg?: string } | undefined)?.msg,
          (row?.warning?.[key] as { msg?: string } | undefined)?.msg
        )
      : (row?.errors?.[key] as { msg?: string } | undefined)?.msg || ''
  }));
}

export function getColumnByType({
  value,
  key,
  type,
  options,
  defaultValue,
  index,
  required,
  ...restProps
}: ColumnByTypeParams): Record<string, unknown> | undefined {
  const row = value ?? {};
  const isSectionDisabled =
    restProps.isSectionDisabled ?? isFcaTableDisabledForFund(row, restProps.tableName);
  const sharedProps = { ...restProps, isSectionDisabled };

  if (isSectionDisabled) {
    return {
      ...sharedProps,
      type: 'customText',
      text: 'N/A',
      dataKey: key,
      dataIndex: index,
      nonEditable: true,
      required: false,
      errorMessage: null
    };
  }

  switch (type) {
    case 'select': {
      return {
        type: type,
        options: options as SelectOption[] | undefined,
        selectedValue: row[key] === undefined ? '' : row[key],
        inputValue: row[key] === undefined ? '' : row[key],
        dataKey: key,
        dataIndex: index,
        isStaticField: row.static_template_fields
          ? row.static_template_fields.includes(key)
          : false,
        validator: () => !(row?.errors && row?.errors[key]),
        required: required && !commonStore.isViewer ? row[key] === defaultValue : false,
        errorMessage:
          !commonStore.isViewer &&
          row?.errors &&
          row?.errors[key] &&
          (row.errors[key] as { msg?: string } | undefined)?.msg
            ? (row.errors[key] as { msg?: string } | undefined)?.msg
            : null,
        ...sharedProps
      };
    }
    case 'customText': {
      return {
        type: type,
        text: row[key],
        isStaticField: row.static_template_fields
          ? row.static_template_fields.includes(key)
          : false,
        dataKey: key,
        dataIndex: index,
        nonEditable: key === 'ranking',
        style:
          key === 'ranking'
            ? {
                background: '#EDF0F5'
              }
            : {},
        validator: () => !(row?.errors && row?.errors[key]),
        required: !commonStore.isViewer && required ? row[key] === defaultValue : false,
        errorMessage:
          !commonStore.isViewer &&
          row?.errors &&
          row?.errors[key] &&
          (row.errors[key] as { msg?: string } | undefined)?.msg
            ? (row.errors[key] as { msg?: string } | undefined)?.msg
            : null,
        ...sharedProps
      };
    }
    case 'customNumber': {
      return {
        type: type,
        value: row[key],
        dataKey: key,
        dataIndex: index,
        isStaticField: row.static_template_fields
          ? row.static_template_fields.includes(key)
          : false,
        validator: () => !(row?.errors && row?.errors[key]),
        required: !commonStore.isViewer && required ? row[key] === defaultValue : false,
        errorMessage:
          !commonStore.isViewer &&
          row?.errors &&
          row?.errors[key] &&
          (row.errors[key] as { msg?: string } | undefined)?.msg
            ? (row.errors[key] as { msg?: string } | undefined)?.msg
            : null,
        ...sharedProps
      };
    }
    case 'customBoolean': {
      return {
        type: type,
        isStaticField: row.static_template_fields
          ? row.static_template_fields.includes(key)
          : false,
        options: options as SelectOption[] | undefined,
        selectedValue: row[key],
        inputValue: row[key],
        dataKey: key,
        dataIndex: index,
        validator: () => !(row?.errors && row?.errors[key]),
        required: !commonStore.isViewer && required ? row[key] === defaultValue : false,
        errorMessage:
          !commonStore.isViewer &&
          row?.errors &&
          row?.errors[key] &&
          (row.errors[key] as { msg?: string } | undefined)?.msg
            ? (row.errors[key] as { msg?: string } | undefined)?.msg
            : null,
        ...sharedProps
      };
    }
    case 'customDate': {
      return {
        type: type,
        date:
          row[key] !== null && row[key] !== undefined
            ? new Date(row[key] as string | number | Date)
            : null,
        isStaticField: row.static_template_fields
          ? row.static_template_fields.includes(key)
          : false,
        dataKey: key,
        dataIndex: index,
        validator: () => !(row?.errors && row?.errors[key]),
        required: !commonStore.isViewer && required ? row[key] === defaultValue : false,
        errorMessage:
          !commonStore.isViewer &&
          row?.errors &&
          row?.errors[key] &&
          (row.errors[key] as { msg?: string } | undefined)?.msg
            ? (row.errors[key] as { msg?: string } | undefined)?.msg
            : null,
        ...sharedProps
      };
    }

    case 'customDateTime': {
      return {
        type: type,
        date:
          row[key] !== null && row[key] !== undefined
            ? new Date(row[key] as string | number | Date)
            : null,
        isStaticField: row.static_template_fields
          ? row.static_template_fields.includes(key)
          : false,
        dataKey: key,
        dataIndex: index,
        validator: () => !(row?.errors && row?.errors[key]),
        required: !commonStore.isViewer && required ? row[key] === defaultValue : false,
        errorMessage:
          !commonStore.isViewer &&
          row?.errors &&
          row?.errors[key] &&
          (row.errors[key] as { msg?: string } | undefined)?.msg
            ? (row.errors[key] as { msg?: string } | undefined)?.msg
            : null,
        ...sharedProps
      };
    }
  }
}

export function updateDataByType({ cell }: UpdateDataByTypeParams): unknown {
  switch (cell.type) {
    case 'select': {
      return cell.selectedValue;
    }
    case 'checkbox': {
      return cell.checked;
    }
    case 'customText': {
      return cell.text;
    }
    case 'customNumber': {
      return cell.value;
    }
    case 'customBoolean': {
      return cell.selectedValue === 'Yes' ? true : cell.selectedValue === 'No' ? false : null;
    }
    case 'customDate': {
      return isValidDate(cell.date) ? moment(cell.date).format('YYYY-MM-DD') : null;
    }
    case 'customDateTime': {
      return isValidDate(cell.date) ? cell.date : null;
    }
  }
}

export const getDefaultValueByType = (value: SchemaValueDescriptor): string | null | undefined => {
  if (
    (Array.isArray(value.type) ? value.type.includes('string') : value.type!.includes('string')) &&
    value.format === 'date'
  ) {
    return null;
  }

  if (Array.isArray(value.type) ? value.type.includes('string') : value.type!.includes('string')) {
    return '';
  }
  if (
    Array.isArray(value.type)
      ? value.type.includes('integer') || value.type.includes('number')
      : value.type!.includes('integer') || value.type!.includes('number')
  ) {
    return null;
  }

  if (
    Array.isArray(value.type) ? value.type.includes('boolean') : value.type!.includes('boolean')
  ) {
    return null;
  }

  if (Array.isArray(value.type) ? value.type.includes('array') : value.type!.includes('array')) {
    return '';
  }
};

export const getTypeFromSchema = (value: SchemaValueDescriptor): ColumnType | undefined => {
  if (Array.isArray(value.type) ? value.type.includes('array') : value.type!.includes('array')) {
    return 'select';
  }

  if (
    (Array.isArray(value.type) ? value.type.includes('string') : value.type!.includes('string')) &&
    value.format === 'date'
  ) {
    return 'customDate';
  }

  if (
    (Array.isArray(value.type) ? value.type.includes('string') : value.type!.includes('string')) &&
    value.format === 'date-time'
  ) {
    return 'customDateTime';
  }

  if (Array.isArray(value.type) ? value.type.includes('date') : value.type!.includes('date')) {
    return 'customDate';
  }

  if (Array.isArray(value.type) ? value.type.includes('string') : value.type!.includes('string')) {
    return 'customText';
  }
  if (
    Array.isArray(value.type)
      ? value.type.includes('integer') || value.type.includes('number')
      : value.type!.includes('integer') || value.type!.includes('number')
  ) {
    return 'customNumber';
  }

  if (
    Array.isArray(value.type) ? value.type.includes('boolean') : value.type!.includes('boolean')
  ) {
    return 'customBoolean';
  }
};

export const getKeyByType = (type: ColumnType | undefined): ColumnValueKey | undefined => {
  if (type === 'select') {
    return 'selectedValue';
  }

  if (type === 'customText') {
    return 'text';
  }
  if (type === 'customNumber') {
    return 'value';
  }

  if (type === 'customBoolean') {
    return 'selectedValue';
  }

  if (type === 'customDate') {
    return 'date';
  }

  if (type === 'customDateTime') {
    return 'date';
  }
};

export const filterArrayByFieldsWithStop = <T extends FundDataRow>(
  array: T[],
  fields: string[]
): Array<T & { is_deleted?: boolean }> => {
  const result: Array<T & { is_deleted?: boolean }> = [];

  for (const obj of array) {
    if (
      fields
        .filter(field => field !== 'ranking')
        .some(
          field =>
            obj[field] !== '' &&
            obj[field] !== null &&
            obj[field] !== undefined &&
            !Number.isNaN(obj[field])
        )
    ) {
      result.push(obj);
    } else if (obj.id && fields.includes('ranking')) {
      result.push({
        ...obj,
        is_deleted: true
      });
    } else if (obj.id) {
      result.push(obj);
    }
  }

  return result;
};

export const filterArrayByFieldsNotEmptyValue = <T extends FundDataRow>(
  array: T[],
  fields: string[]
): Array<T & { is_deleted?: boolean }> => {
  const result: Array<T & { is_deleted?: boolean }> = [];

  for (const obj of array) {
    if (
      fields
        .filter(field => field !== 'ranking')
        .some(
          field =>
            obj[field] !== '' &&
            obj[field] !== null &&
            obj[field] !== undefined &&
            !Number.isNaN(obj[field])
        )
    ) {
      result.push(obj);
    } else if (obj.id) {
      result.push({
        ...obj,
        is_deleted: true
      });
    }
  }

  return result;
};

export const filterArrayByFieldsForMultipleTable = <T extends FundDataRow>(
  array: T[],
  fields: string[]
): Array<T & { is_deleted?: boolean }> => {
  const result: Array<T & { is_deleted?: boolean }> = [];

  for (const obj of array) {
    if (
      fields.some(
        field =>
          obj[field] !== '' &&
          obj[field] !== null &&
          obj[field] !== undefined &&
          !Number.isNaN(obj[field])
      )
    ) {
      result.push(obj);
    } else if (obj.id) {
      result.push({
        ...obj,
        is_deleted: true
      });
    } else {
      result.push(obj);
    }
  }

  return result;
};

interface FieldValidationRules {
  string?: {
    maxLength?: number;
    minLength?: number;
    pattern?: string;
  };
  number?: {
    maximum?: number;
    minimum?: number;
  };
  integer?: {
    maximum?: number;
    minimum?: number;
  };
}

export function validateFieldRules(
  value: unknown,
  rules?: FieldValidationRules
): string | null | undefined {
  if (!rules) return undefined;

  const strRules = rules.string || {};
  const numRules = rules.number || rules.integer || {};
  const hasRules = Object.keys(strRules).length > 0 || Object.keys(numRules).length > 0;

  if (!hasRules) return undefined;
  if (value === null || value === undefined || value === '') return null;

  if (typeof value === 'string') {
    if (strRules.maxLength !== undefined && value.length > strRules.maxLength)
      return `Must be at most ${strRules.maxLength} characters`;
    if (strRules.minLength !== undefined && value.length < strRules.minLength)
      return `Must be at least ${strRules.minLength} characters`;
    if (strRules.pattern && !new RegExp(strRules.pattern).test(value)) return 'Invalid format';
  }

  if (typeof value === 'number') {
    if (numRules.maximum !== undefined && value > numRules.maximum)
      return `Must be at most ${numRules.maximum}`;
    if (numRules.minimum !== undefined && value < numRules.minimum)
      return `Must be at least ${numRules.minimum}`;
  }

  return null;
}
